import { extractTextFromFile } from './assessmentService';
import { transcribeImage, transformRubricDocument } from './aiService';
import { RubricBand, RubricCriterion, RubricTransformContext, RubricTransformResponse } from '../types';

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif'
];

const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.heic', '.heif'];
const DOC_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain'
];
const DOC_EXTENSIONS = ['.pdf', '.docx', '.txt'];

export const RUBRIC_UPLOAD_ACCEPT = [
  ...DOC_EXTENSIONS,
  ...IMAGE_EXTENSIONS
].join(',');

const MAX_FILE_SIZE_BYTES = 12 * 1024 * 1024; // 12MB safety limit per file
const MAX_RUBRIC_TEXT_LENGTH = 16000;

export interface RubricTextChunk {
  name: string;
  kind: 'document' | 'image';
  text: string;
}

export interface RubricTextExtractionResult {
  combinedText: string;
  chunks: RubricTextChunk[];
  failedFiles: Array<{ name: string; reason: string }>;
}

export interface StructuredRubricResult {
  criteria: RubricCriterion[];
  warnings: string[];
  summary: string[];
  extractedText: string;
  fileSummaries: Array<{ name: string; kind: 'document' | 'image' }>;
  failedFiles: Array<{ name: string; reason: string }>;
  rawModel?: RubricTransformResponse;
}

const isImageFile = (file: File) => {
  const lowerName = file.name.toLowerCase();
  return IMAGE_MIME_TYPES.includes(file.type) || IMAGE_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};

const isDocumentFile = (file: File) => {
  const lowerName = file.name.toLowerCase();
  return DOC_MIME_TYPES.includes(file.type) || DOC_EXTENSIONS.some(ext => lowerName.endsWith(ext));
};

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error(`Failed to read ${file.name}`));
        return;
      }
      const base64 = reader.result.split(',')[1] || '';
      if (!base64) {
        reject(new Error(`Unable to convert ${file.name} to base64.`));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error(`Could not read ${file.name}`));
    reader.readAsDataURL(file);
  });
};

export const extractRubricTextFromFiles = async (
  files: File[],
  onProgress?: (status: string) => void
): Promise<RubricTextExtractionResult> => {
  if (!files || files.length === 0) {
    throw new Error('Please select at least one file to analyse.');
  }

  const chunks: RubricTextChunk[] = [];
  const failedFiles: Array<{ name: string; reason: string }> = [];

  for (let index = 0; index < files.length; index++) {
    const file = files[index];

    if (file.size > MAX_FILE_SIZE_BYTES) {
      failedFiles.push({ name: file.name, reason: 'File exceeds the 12MB limit.' });
      continue;
    }

    try {
      if (isDocumentFile(file)) {
        onProgress?.(`Extracting text from ${file.name}...`);
        const text = await extractTextFromFile(file);
        if (!text.trim()) {
          throw new Error('No text detected in this document.');
        }
        chunks.push({
          name: file.name,
          kind: 'document',
          text: text.trim()
        });
      } else if (isImageFile(file)) {
        onProgress?.(`Transcribing ${file.name} (${index + 1}/${files.length})...`);
        const base64 = await readFileAsBase64(file);
        const text = await transcribeImage(base64);
        chunks.push({
          name: file.name,
          kind: 'image',
          text: text.trim()
        });
      } else {
        failedFiles.push({ name: file.name, reason: 'Unsupported file type.' });
      }
    } catch (error) {
      const reason = error instanceof Error ? error.message : 'Unknown error processing file.';
      failedFiles.push({ name: file.name, reason });
    }
  }

  if (chunks.length === 0) {
    const reasons = failedFiles.map(f => `${f.name}: ${f.reason}`).join('; ');
    throw new Error(reasons || 'We could not extract any text from the uploaded files.');
  }

  const combinedText = chunks
    .map(chunk => `### Source: ${chunk.name} (${chunk.kind})\n${chunk.text}`)
    .join('\n\n');

  return {
    combinedText,
    chunks,
    failedFiles
  };
};

const sanitizeDescriptor = (text: string, fallback: string) => {
  const cleaned = (text || '').replace(/\s+/g, ' ').trim();
  return cleaned || fallback;
};

const buildBandId = (critIndex: number, bandIndex: number) => {
  return `rubric-import-${critIndex}-${bandIndex}-${Date.now()}-${Math.round(Math.random() * 1000)}`;
};

const generateEvenRanges = (bandCount: number, totalMarks: number) => {
  const safeBandCount = Math.max(1, Math.min(bandCount, totalMarks || bandCount));
  const baseSize = Math.floor(totalMarks / safeBandCount);
  let remainder = totalMarks % safeBandCount;
  const ranges: Array<{ min: number; max: number }> = [];
  let currentMin = 1;

  for (let i = 0; i < safeBandCount; i++) {
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    const segmentSize = Math.max(1, baseSize + extra);
    const candidateMax = i === safeBandCount - 1 ? totalMarks : currentMin + segmentSize - 1;
    ranges.push({ min: currentMin, max: candidateMax });
    currentMin = candidateMax + 1;
  }

  if (ranges.length) {
    ranges[ranges.length - 1].max = totalMarks;
  }

  return ranges;
};

const normalizeBands = (
  rawBands: any[],
  critIndex: number,
  totalMarks: number,
  criterionName: string,
  warnings: string[]
): RubricBand[] => {
  const filteredBands = rawBands.filter(Boolean);
  if (filteredBands.length === 0) {
    warnings.push(`No band descriptors detected for "${criterionName}". Created fallback bands.`);
    const fallbackRanges = generateEvenRanges(4, totalMarks);
    return fallbackRanges.map((range, idx) => ({
      id: buildBandId(critIndex, idx),
      min: range.min,
      max: range.max,
      descriptor: `Placeholder descriptor for band ${idx + 1}. Please edit this text.`
    }));
  }

  const parsed = filteredBands.map((band, idx) => {
    let min = typeof band.minScore === 'number' ? Math.floor(band.minScore) : null;
    let max = typeof band.maxScore === 'number' ? Math.floor(band.maxScore) : null;

    if ((!min || !max) && typeof band.scoreRange === 'string') {
      const match = band.scoreRange.match(/(-?\d+)\s*(?:-|to)?\s*(-?\d+)/i);
      if (match) {
        min = parseInt(match[1], 10);
        max = parseInt(match[2], 10);
      }
    }

    const descriptor = sanitizeDescriptor(
      band.descriptor || band.expectations || band.description || band.notes,
      `Descriptor missing for ${criterionName} band ${idx + 1}.`
    );

    return {
      id: buildBandId(critIndex, idx),
      min,
      max,
      descriptor: band.bandLabel ? `${band.bandLabel}: ${descriptor}` : descriptor
    };
  });

  const hasValidCoverage = parsed.every(b => typeof b.min === 'number' && typeof b.max === 'number') &&
    parsed.some(b => b.min === 1) &&
    parsed.some(b => b.max === totalMarks);

  if (!hasValidCoverage) {
    warnings.push(`Adjusted band ranges for "${criterionName}" to cover 1-${totalMarks}.`);
    const fallbackRanges = generateEvenRanges(parsed.length, totalMarks);
    return parsed.map((band, idx) => {
      const fallback =
        fallbackRanges[idx] ||
        fallbackRanges[fallbackRanges.length - 1] ||
        { min: 1, max: totalMarks };
      return {
        ...band,
        min: fallback.min,
        max: fallback.max
      };
    });
  }

  // Ensure sorted order and contiguity
  const sorted = [...parsed].sort((a, b) => (a.min as number) - (b.min as number));
  let cursor = 1;
  for (let i = 0; i < sorted.length; i++) {
    const band = sorted[i];
    if ((band.min as number) !== cursor) {
      warnings.push(`Fixed a gap before band ${i + 1} of "${criterionName}".`);
      band.min = cursor;
    }
    cursor = (band.max as number) + 1;
  }
  if (sorted.length) {
    sorted[sorted.length - 1].max = totalMarks;
  }

  return sorted;
};

const normalizeWeights = (criteria: RubricCriterion[], warnings: string[]) => {
  const weightSum = criteria.reduce((sum, c) => sum + (Number(c.weight) || 0), 0);
  if (criteria.length === 0) {
    return;
  }

  if (!weightSum) {
    const evenWeight = Math.floor(100 / criteria.length);
    criteria.forEach((criterion, idx) => {
      criterion.weight = idx === criteria.length - 1 ? 100 - evenWeight * (criteria.length - 1) : evenWeight;
    });
    warnings.push('Weights were missing, so we evenly distributed them across all criteria.');
    return;
  }

  if (weightSum !== 100) {
    const ratio = 100 / weightSum;
    let runningTotal = 0;
    criteria.forEach((criterion, idx) => {
      if (idx === criteria.length - 1) {
        criterion.weight = Math.max(0, 100 - runningTotal);
      } else {
        const normalized = Math.max(1, Math.round((Number(criterion.weight) || 0) * ratio));
        criterion.weight = normalized;
        runningTotal += normalized;
      }
    });
    warnings.push('Weights did not sum to 100%. They have been normalized automatically.');
  }
};

const buildCriteriaFromModel = (
  model: RubricTransformResponse,
  totalMarks: number
): { criteria: RubricCriterion[]; warnings: string[]; summary: string[] } => {
  const warnings: string[] = [];
  const summary = Array.isArray(model.summary) ? model.summary : [];
  const modelWarnings = Array.isArray(model.warnings) ? model.warnings : [];
  warnings.push(...modelWarnings);

  const rawCriteria = Array.isArray(model.criteria) ? model.criteria : [];
  if (rawCriteria.length === 0) {
    throw new Error('The AI response did not include any criteria. Please try again or upload a clearer rubric.');
  }

  const normalizedCriteria: RubricCriterion[] = rawCriteria.map((criterion, index) => {
    const weight =
      typeof criterion.weightPercent === 'number'
        ? Math.round(criterion.weightPercent)
        : typeof (criterion as any).weight === 'number'
          ? Math.round((criterion as any).weight)
          : 0;

    const normalizedBands = normalizeBands(
      Array.isArray((criterion as any).bands) ? (criterion as any).bands : [],
      index,
      totalMarks,
      criterion.name || `Criterion ${index + 1}`,
      warnings
    );

    return {
      id: `rubric-criterion-${index}-${Date.now()}`,
      name: criterion.name || `Criterion ${index + 1}`,
      weight,
      bands: normalizedBands
    };
  });

  normalizeWeights(normalizedCriteria, warnings);
  return { criteria: normalizedCriteria, warnings, summary };
};

export const convertRubricFilesToCriteria = async (
  files: File[],
  context: RubricTransformContext,
  hooks?: { onStatus?: (status: string) => void; modelId?: string }
): Promise<StructuredRubricResult> => {
  if (!context.totalMarks || context.totalMarks <= 0) {
    throw new Error('Enter the total marks for this assessment before converting a rubric.');
  }

  const extraction = await extractRubricTextFromFiles(files, hooks?.onStatus);
  const truncatedText =
    extraction.combinedText.length > MAX_RUBRIC_TEXT_LENGTH
      ? extraction.combinedText.slice(0, MAX_RUBRIC_TEXT_LENGTH)
      : extraction.combinedText;

  if (extraction.combinedText.length > MAX_RUBRIC_TEXT_LENGTH) {
    hooks?.onStatus?.('Large rubric detected. Truncating to keep the important sections.');
  } else {
    hooks?.onStatus?.('Transforming rubric into structured criteria...');
  }

  const modelResponse = await transformRubricDocument(truncatedText, context, hooks?.modelId);
  const { criteria, warnings, summary } = buildCriteriaFromModel(modelResponse, context.totalMarks);

  const failureMessages = extraction.failedFiles.map(f => `${f.name}: ${f.reason}`);
  warnings.push(...failureMessages);

  if (extraction.combinedText.length > MAX_RUBRIC_TEXT_LENGTH) {
    warnings.push('Only the first 16k characters of the rubric were sent to the AI to avoid token limits.');
  }

  return {
    criteria,
    warnings,
    summary,
    extractedText: truncatedText,
    fileSummaries: extraction.chunks.map(chunk => ({ name: chunk.name, kind: chunk.kind })),
    failedFiles: extraction.failedFiles,
    rawModel: modelResponse
  };
};
